import type { AnimationBreakPoint, AnimationControlSetting, BlockAbstract } from '@lighthouse/core'
import { AnimationTypeEnum, PresetEnum } from '@lighthouse/core'
import { useApplicationContext, useFillPickerContext } from '@lighthouse/shared'
import { mergeRefs, nanoid } from '@lighthouse/tools'
import type {
    AnimationDefinition,
    AnimationOptions,
    AnimationPlaybackControlsWithThen,
    HTMLMotionProps,
    MotionProps,
    ObjectTarget,
    UseInViewOptions
} from 'motion/react'
import { motion, useAnimate, useInView } from 'motion/react'
import { findLast, mergeDeepRight } from 'rambda'
import type { RefObject } from 'react'
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAnimationContext, useContainerBlockContext, useCustomViewBlockContext, usePanelContext } from '../../context'
import { animationLayoutIdRegistry } from '../../utils/AnimationLayoutIdRegistry'
import {
    getAnimation,
    getBuilderHoverAnimationConfig,
    getBuilderLayoutAnimationConfig,
    getDefaultAnimationConfig,
    getLayoutAnimationConfig,
    getParentScrollContainer,
    getTransition,
    getVisibleHeight
} from './utils'

interface AnimationDecoratorsProps extends HTMLMotionProps<'div'> {
    blockId: string
    blockName?: string
    className?: string
    animation?: AnimationBreakPoint
    onBlockChange?: (values: BlockAbstract, origin: BlockAbstract) => Promise<void> | void
    children?: React.ReactNode
}

const MotionDiv = motion.create('div')

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const useAnimationInView = (
    ref: RefObject<Element | null>,
    props: UseInViewOptions & { start: 'top' | 'bottom' | 'center'; disabled?: boolean }
) => {
    const { start, disabled, ...rest } = props
    const isInView = useInView(ref, rest)

    const [isIn, setIsIn] = useState<boolean | null>(null)
    const scrollHandlerRef = useRef((e: Event) => {
        if (ref.current && e.target instanceof HTMLElement) {
            const rect = ref.current.getBoundingClientRect()
            const scrollRect = e.target.getBoundingClientRect()
            const visitHeight = getVisibleHeight(ref.current)
            if (start === 'top') {
                setIsIn(visitHeight > 0)
                return
            }
            if (start === 'center') {
                setIsIn(visitHeight >= rect.height / 2)
                return
            }
            if (start === 'bottom') {
                const isScrollBottom = rect.bottom < scrollRect.bottom
                setIsIn(visitHeight >= rect.height - 12 || isScrollBottom)
            }
        }
    })

    useEffect(() => {
        if (!ref.current || disabled) {
            return
        }
        const scrollContainer = getParentScrollContainer(ref.current)
        const target: HTMLElement | Window = scrollContainer ?? window
        const handler = scrollHandlerRef.current
        target.addEventListener('scroll', handler)
        target.addEventListener('resize', handler)

        // 主动计算一次，避免“明明可见但 inView 仍为 false”
        if (scrollContainer) {
            scrollHandlerRef.current({ target: scrollContainer } as unknown as Event)
        } else {
            const visitHeight = ref.current ? getVisibleHeight(ref.current) : 0
            setIsIn(visitHeight > 0)
        }

        return () => {
            target.removeEventListener('scroll', handler)
            target.removeEventListener('resize', handler)
        }
    }, [ref, disabled])

    // IntersectionObserver 在某些滚动容器/布局下会失效；只要我们已计算出 isIn，就以 isIn 为准
    return isIn === null ? isInView : isIn || isInView
}

export const AnimationDecorators = memo(
    forwardRef<HTMLDivElement, AnimationDecoratorsProps>(
        ({ blockId, blockName, animation, children, style: propsStyle, onBlockChange, ...rest }, ref) => {
            const { palettes } = useFillPickerContext()
            const { appId, isBuilder } = useApplicationContext()
            const { scope, parentPanelSmartAnimation, layerInViewOnce } = useContainerBlockContext()
            const { hasSmartAnimationParentPanel } = usePanelContext()
            const { record } = useCustomViewBlockContext()
            const animationKeyRef = useRef('init')
            const onceRef = useRef(false)
            const animationControlRef = useRef<AnimationPlaybackControlsWithThen | null>(null)
            const { queue, onAnimationComplete, onAnimationStart } = useAnimationContext()
            const {
                blockId: ctxBlockId,
                played: ctxPlayed,
                scope: ctxScope,
                type: ctxType
            } = useMemo(
                () =>
                    findLast(v => v.blockId === blockId && v.scope === scope, queue) ?? {
                        blockId: ''
                    },
                [blockId, queue, scope]
            )
            const isActive = blockId === ctxBlockId && ctxScope === scope

            const [animateRef, animate] = useAnimate<HTMLDivElement>()

            const { layerInView, loop, hover, panelChange } = animation ?? {}
            const { replay, start = 'top', enter } = layerInView ?? {}

            // 是否应该运行运行时动画
            const shouldRunRuntimeAnimation = !isBuilder && !!appId

            // replay=false 时，容器多面板切换会导致重新挂载；若 once 已记录，则不应再次应用 enter 的 initial（避免落回隐藏初始态）
            const externalOnce = layerInViewOnce && layerInViewOnce.ownerBlockId === blockId ? layerInViewOnce : undefined
            const playedLayerInViewOnce = externalOnce ? externalOnce.mapRef.current.get(externalOnce.key) ?? false : false
            const shouldSkipLayerInViewInitial =
                shouldRunRuntimeAnimation && !!layerInView?.enter && !layerInView?.replay && !!playedLayerInViewOnce

            const inView = useAnimationInView(animateRef, {
                start,
                once: !replay,
                disabled: !shouldRunRuntimeAnimation || !layerInView?.enter
            })

            // 生成动画配置的通用方法
            const createAnimationConfig = useCallback(
                (animation?: AnimationControlSetting, options = {}) => {
                    if (!animation || !appId) {
                        return null
                    }
                    return {
                        animate: getAnimation({ appId, animation, palettes }, options),
                        transition: getTransition(animation.transition)
                    }
                },
                [appId, palettes]
            )

            // 处理进入动画
            const handleEnterAnimation = useCallback(async () => {
                if (!layerInView?.enter || !appId) {
                    return
                }
                // 若不运行退出动画时，又需要重新执行进入动画，则短暂播放一个退出动效，还原为播放动效前的初始状态
                if (!layerInView?.isRunTimeExitAnimate) {
                    const resetConfig = createAnimationConfig(layerInView.enter)
                    if (resetConfig) {
                        await animate(animateRef.current, resetConfig.animate, { duration: 0.01 })
                    }
                }
                // 执行进入动画
                const enterConfig = {
                    animate: getLayoutAnimationConfig({ appId, animation: layerInView.enter, palettes }),
                    transition: getTransition(layerInView.enter.transition)
                }
                await animate(animateRef.current, enterConfig.animate, enterConfig.transition as AnimationOptions)
            }, [layerInView?.enter, layerInView?.isRunTimeExitAnimate, appId, palettes, animate, animateRef, createAnimationConfig])

            // 处理退出动画
            const handleExitAnimation = useCallback(() => {
                if (!layerInView?.enter || !layerInView?.isRunTimeExitAnimate) {
                    return
                }

                const exitConfig = createAnimationConfig(layerInView.enter)
                if (exitConfig) {
                    animate(animateRef.current, exitConfig.animate, exitConfig.transition as AnimationOptions)
                }
            }, [layerInView?.enter, layerInView?.isRunTimeExitAnimate, createAnimationConfig, animate, animateRef])

            // 管理端预览
            const getAnimateBuilder = useCallback(
                (type: AnimationTypeEnum) => {
                    const { enter } = layerInView ?? {}
                    // 应用端禁用
                    if (!appId || !isBuilder) {
                        return
                    }
                    // 需要播放的动画blockId和scope匹配上时
                    if (isActive) {
                        switch (type) {
                            case AnimationTypeEnum.loop: {
                                if (!loop?.effect) {
                                    return
                                }
                                const transition = getTransition(loop?.effect?.transition, {
                                    repeat: Infinity,
                                    repeatType: loop.effect.repeatType ?? 'loop'
                                })
                                return {
                                    // key: animationKeyRef.current,
                                    animate: getAnimation({ appId, animation: loop?.effect, palettes }),
                                    transition: {
                                        ...transition,
                                        duration: loop?.effect?.transition.time,
                                        visualDuration: loop?.effect?.transition.time
                                    }
                                }
                            }
                            case AnimationTypeEnum.hover: {
                                if (!hover?.effect) {
                                    return
                                }
                                const transition = getTransition(hover?.effect?.transition)
                                return (
                                    hover?.effect && {
                                        // key: animationKeyRef.current,
                                        animate: getBuilderHoverAnimationConfig(
                                            { appId, animation: hover?.effect, palettes },
                                            {
                                                styles: propsStyle as React.CSSProperties
                                            }
                                        ),
                                        transition: {
                                            ...transition,
                                            duration: hover?.effect?.transition.time,
                                            visualDuration: hover?.effect?.transition.time
                                        }
                                    }
                                )
                            }
                            default: {
                                if (!enter) {
                                    return
                                }
                                return {
                                    // 设置动画css起始值
                                    key: animationKeyRef.current,
                                    // style: getAnimationStyles({ appId, animation: enter, palettes }),
                                    // initial: getAnimation({ appId, animation: enter, palettes }),
                                    animate: getBuilderLayoutAnimationConfig({ appId, animation: enter, palettes }),
                                    transition: getTransition(enter?.transition)
                                }
                            }
                        }
                    }
                },
                [appId, hover?.effect, isActive, isBuilder, layerInView, loop?.effect, palettes, propsStyle]
            )
            const onCannelBuilderAnimate = useCallback(() => {
                if (!appId || !isBuilder) {
                    return
                }

                // cancel 会回到initial状态，complete 会直接停止动画到结束状态
                animationControlRef.current?.cancel()
            }, [appId, isBuilder])

            const onPlayBuilderAnimate = useCallback(
                (type?: AnimationTypeEnum) => {
                    if (!appId || !isBuilder || !type) {
                        return
                    }
                    const animateBuilder = getAnimateBuilder(type)
                    if (animateBuilder) {
                        animationControlRef.current?.cancel()
                        animationKeyRef.current = nanoid()
                        onAnimationStart?.({ blockId, scope })
                        animationControlRef.current = animate(
                            animateRef.current,
                            animateBuilder.animate as ObjectTarget<HTMLDivElement>,
                            // @HACK 如果更新key引发对象更新，从而导致动效执行
                            {
                                key: animationKeyRef.current,
                                ...animateBuilder.transition
                            } as unknown as AnimationOptions
                        )
                        animationControlRef.current
                            .then(() => {
                                if (type !== 'loop') {
                                    onAnimationComplete?.({ blockId, scope })
                                }
                            })
                            .catch(() => {
                                //
                            })
                    }
                },
                [animate, animateRef, appId, blockId, getAnimateBuilder, isBuilder, onAnimationComplete, onAnimationStart, scope]
            )

            // // 动画执行后style变化时更新样式
            // useEffect(() => {
            //     if (animationKeyRef.current !== 'init') {
            //         animate(animateRef.current, propsStyle, {
            //             duration: 0.1
            //         })
            //     }
            // }, [animate, animateRef, propsStyle])

            // 管理端动画
            useEffect(() => {
                if (isActive) {
                    ctxPlayed ? onPlayBuilderAnimate(ctxType) : onCannelBuilderAnimate()
                }
            }, [ctxPlayed, ctxType, isActive, onCannelBuilderAnimate, onPlayBuilderAnimate])

            // 应用端出现动画
            useEffect(() => {
                if (!shouldRunRuntimeAnimation || !layerInView?.enter) {
                    return
                }

                const externalOnce = layerInViewOnce && layerInViewOnce.ownerBlockId === blockId ? layerInViewOnce : undefined
                const onceValue = externalOnce ? externalOnce.mapRef.current.get(externalOnce.key) ?? false : onceRef.current
                const isReplayAnimate = layerInView?.replay ? true : !onceValue

                if (!isReplayAnimate) {
                    return
                }

                const nextOnce = inView || onceValue
                if (externalOnce) {
                    externalOnce.mapRef.current.set(externalOnce.key, nextOnce)
                } else {
                    onceRef.current = nextOnce
                }

                if (inView) {
                    handleEnterAnimation()
                } else if (layerInView?.isRunTimeExitAnimate) {
                    handleExitAnimation()
                }
            }, [
                shouldRunRuntimeAnimation,
                layerInView?.enter,
                layerInView?.replay,
                layerInView?.isRunTimeExitAnimate,
                layerInViewOnce,
                blockId,
                inView,
                handleEnterAnimation,
                handleExitAnimation
            ])

            // 应用端循环动画
            useEffect(() => {
                if (!shouldRunRuntimeAnimation || !loop?.effect) {
                    return
                }

                const loopConfig = {
                    animate: getAnimation({ appId, animation: loop.effect, palettes }, { diffAnimate: getDefaultAnimationConfig() }),
                    transition: getTransition(loop.effect.transition, {
                        repeat: Infinity,
                        repeatType: loop.effect.repeatType ?? 'loop'
                    })
                }

                animate(animateRef.current, loopConfig.animate, loopConfig.transition as AnimationOptions)
            }, [shouldRunRuntimeAnimation, loop?.effect, appId, palettes, animate, animateRef])

            // 管理段应用端面板切换动画
            useEffect(() => {
                if (!appId || !panelChange?.effect || panelChange.effect.preset !== PresetEnum.fadeIn) {
                    return
                }
                const panelChangeConfig = {
                    animate: getAnimation({ appId, animation: panelChange?.effect, palettes }),
                    transition: getTransition(panelChange?.effect?.transition)
                }
                animate(animateRef.current, panelChangeConfig.animate, panelChangeConfig.transition as AnimationOptions)
            }, [shouldRunRuntimeAnimation, panelChange?.effect, appId, palettes, animate, animateRef, panelChange])

            const animationRunner = useMemo(() => {
                if (!appId) {
                    return {}
                }
                // 管理端禁用原本动画
                if (isBuilder) {
                    return {}
                }

                return {
                    initial: shouldSkipLayerInViewInitial
                        ? undefined
                        : enter &&
                          getAnimation({
                              appId,
                              animation: enter,
                              palettes
                          }),
                    // animate: loop?.effect && {
                    //     ...getAnimation({
                    //         appId,
                    //         animation: loop?.effect,
                    //         palettes
                    //     }),
                    //     // rotate:360,
                    //     transition: getTransition(loop?.effect?.transition, { repeat: Infinity, repeatType: loop.effect.repeatType ?? 'loop' })
                    // },
                    whileHover: hover?.effect && {
                        ...getAnimation(
                            {
                                appId,
                                animation: hover?.effect,
                                palettes
                            },
                            {
                                diffAnimate: getDefaultAnimationConfig()
                            }
                        ),
                        // scale:1.5,
                        transition: getTransition(hover?.effect?.transition)
                    }
                    // whileInView: enter && {
                    //     ...getDefaultAnimationConfig(),
                    //     transition: getTransition(enter?.transition)
                    // },
                    // enter: enter && {
                    //     ...getDefaultAnimationConfig(),
                    //     transition: getTransition(enter?.transition)
                    // },
                    // exit: exit && {
                    //     ...getDefaultAnimationConfig(),
                    //     transition: getTransition(exit?.transition)
                    // }
                }
            }, [appId, enter, hover?.effect, isBuilder, palettes, shouldSkipLayerInViewInitial])
            const scopeKey = useMemo(() => {
                if (!record && !scope) {
                    return undefined
                } else if (record && scope) {
                    return `${scope}:${record.id}`
                }
                return scope || record?.id
            }, [record, scope])

            // 智能动画：注册blockName并获取唯一的layoutId
            // scopeKey 用于隔离不同实例：自定义视图用 recordId，同步组件用 scope
            const uniqueLayoutId = useMemo(() => {
                if (!parentPanelSmartAnimation || !hasSmartAnimationParentPanel || !blockName) {
                    return undefined
                }

                return animationLayoutIdRegistry.register(hasSmartAnimationParentPanel, blockId, blockName, scopeKey) ?? undefined
            }, [blockId, parentPanelSmartAnimation, blockName, scopeKey, hasSmartAnimationParentPanel])

            // 智能动画：添加默认的淡入淡出动画
            const smartAnimationFallback = useMemo(() => {
                if (!parentPanelSmartAnimation) {
                    return {}
                }

                const transition = getTransition(parentPanelSmartAnimation.animation.effect.transition)

                return {
                    // initial: { opacity: 0 },
                    // animate: { opacity: 1 },
                    // exit: { opacity: 0 },
                    transition
                }
            }, [parentPanelSmartAnimation])

            const mergeMotionValue = useCallback((internal: unknown, external: unknown) => {
                if (external === undefined) {
                    return internal
                }
                if (isPlainObject(internal) && isPlainObject(external)) {
                    return { ...internal, ...external }
                }
                return external
            }, [])

            const internalMotionProps = useMemo<MotionProps>(
                () => mergeDeepRight(animationRunner, smartAnimationFallback),
                [animationRunner, smartAnimationFallback]
            )
            const {
                initial: restInitial,
                animate: restAnimate,
                exit: restExit,
                transition: restTransition,
                layout: restLayout,
                layoutId: restLayoutId,
                layoutCrossfade: restLayoutCrossfade,
                whileHover: restWhileHover,
                whileTap: restWhileTap,
                whileInView: restWhileInView,
                viewport: restViewport,
                variants: restVariants,
                drag: restDrag,
                dragConstraints: restDragConstraints,
                dragElastic: restDragElastic,
                dragMomentum: restDragMomentum,
                onUpdate: restOnUpdate,
                onAnimationStart: restOnAnimationStart,
                onAnimationComplete: restOnAnimationComplete,
                ...restProps
            } = rest
            const {
                initial: internalInitial,
                animate: internalAnimate,
                exit: internalExit,
                transition: internalTransition,
                ...internalProps
            } = internalMotionProps

            const mergedInitial = mergeMotionValue(internalInitial, restInitial)
            const mergedAnimate = mergeMotionValue(internalAnimate, restAnimate)
            const mergedExit = mergeMotionValue(internalExit, restExit)
            const mergedTransition = mergeMotionValue(internalTransition, restTransition)

            const shouldUseMotionElement = useMemo(() => {
                return (
                    !!uniqueLayoutId ||
                    !!animation?.layerInView?.enter ||
                    !!animation?.loop?.effect ||
                    !!animation?.hover?.effect ||
                    !!animation?.panelChange?.effect ||
                    restInitial !== undefined ||
                    restAnimate !== undefined ||
                    restExit !== undefined ||
                    restTransition !== undefined ||
                    restLayout !== undefined ||
                    restLayoutId !== undefined ||
                    restLayoutCrossfade !== undefined ||
                    restWhileHover !== undefined ||
                    restWhileTap !== undefined ||
                    restWhileInView !== undefined ||
                    restViewport !== undefined ||
                    restVariants !== undefined ||
                    restDrag !== undefined ||
                    restDragConstraints !== undefined ||
                    restDragElastic !== undefined ||
                    restDragMomentum !== undefined ||
                    restOnUpdate !== undefined
                )
            }, [
                animation?.hover?.effect,
                animation?.layerInView?.enter,
                animation?.loop?.effect,
                animation?.panelChange?.effect,
                restAnimate,
                restDrag,
                restDragConstraints,
                restDragElastic,
                restDragMomentum,
                restExit,
                restInitial,
                restLayout,
                restLayoutCrossfade,
                restLayoutId,
                restOnUpdate,
                restTransition,
                restVariants,
                restViewport,
                restWhileHover,
                restWhileInView,
                restWhileTap,
                uniqueLayoutId
            ])

            if (!shouldUseMotionElement) {
                return (
                    <div
                        ref={ref}
                        style={propsStyle as React.CSSProperties | undefined}
                        {...(restProps as React.HTMLAttributes<HTMLDivElement>)}
                    >
                        {children}
                    </div>
                )
            }

            return (
                <MotionDiv
                    ref={mergeRefs([ref, animateRef])}
                    onAnimationComplete={(arg: AnimationDefinition) => {
                        onAnimationComplete?.({ blockId, scope })
                        restOnAnimationComplete?.(arg)
                    }}
                    onAnimationStart={(arg: AnimationDefinition) => {
                        onAnimationStart?.({ blockId, scope })
                        restOnAnimationStart?.(arg)
                    }}
                    layoutId={uniqueLayoutId}
                    layout={!!uniqueLayoutId}
                    layoutCrossfade={false}
                    {...internalProps}
                    {...restProps}
                    initial={mergedInitial}
                    animate={mergedAnimate}
                    exit={mergedExit}
                    transition={mergedTransition}
                    style={propsStyle}
                >
                    {children}
                </MotionDiv>
            )
        }
    )
)

AnimationDecorators.displayName = 'AnimationDecorators'
